export const ABOUT_OVERVIEW_V2 = {
  sectionLabels: [
    'Core capabilities',
    'Architecture layers',
    'Getting started — 5 steps',
    'Roles',
    'v2.0 known limits',
    'Future Enhancements',
    'Chat Starter Kit v2.0',
  ],
  hero: {
    eyebrow: 'Enterprise AI Platform',
    chips: ['v2.0.0', 'July 2026'],
    title: 'Full-stack AI infrastructure for the enterprise',
    desc: 'Production-grade, multi-tenant platform for intelligent chat, knowledge retrieval, and RFP intelligence — with five knowledge connectors, automated pipelines, multi-tier budgeting, and enterprise guardrails at every layer.',
    badges: [
      { icon: 'CloudSyncOutlined', label: '5 knowledge connectors' },
      { icon: 'LockOutlined', label: 'JWT / RBAC + document roles' },
      { icon: 'SafetyOutlined', label: '4-layer guardrails + audit' },
      { icon: 'FundOutlined', label: 'Multi-tier budgeting' },
      { icon: 'RobotOutlined', label: 'Streaming chat & deep agent' },
      { icon: 'SearchOutlined', label: 'BM25 + vector + GraphRAG' },
    ],
  },
  capabilities: [
    {
      icon: 'CloudSyncOutlined',
      title: 'Multi-connector knowledge',
      desc: 'Ingest from Amazon S3, Confluence, GitHub, Jira, and Azure DevOps Wiki. Verify credentials before save; sync on schedule, webhook, or poll with monitored page/issue updates.',
    },
    {
      icon: 'SyncOutlined',
      title: 'Automated RAG pipeline',
      desc: 'End-to-end file pipeline: Markdown conversion, guardrails, chunking, embedding, and Milvus indexing. Run now, schedule, or trigger via S3 events — with retry, cancel, reprocess, and stale-job recovery.',
    },
    {
      icon: 'MessageOutlined',
      title: 'Intelligent chat & agents',
      desc: 'Multi-agent supervisor routes between RAG and GraphRAG agents. Streaming SSE responses with live thinking steps, deep-agent mode, conversation memory, chat modes, and inline document preview.',
    },
    {
      icon: 'SafetyCertificateOutlined',
      title: '4-layer guardrails',
      desc: 'Input, pre-embedding, context (retrieval), and output guards — PII detection, topic filtering, and NSFW filtering. Admin guardrail audit captures blocked outputs for review.',
    },
    {
      icon: 'FundOutlined',
      title: 'Multi-tier budgeting',
      desc: 'Group budget pools, per-project caps, and per-user allocations. Extension requests, hard budget enforcement (402), My Usage dashboard, and utilization charts for admins.',
    },
    {
      icon: 'FileTextOutlined',
      title: 'RFP / Solution Intelligence',
      desc: 'Dedicated RFP project type with hierarchical corpus layout, labeled chunk store, similar-RFP search, draft synthesis from past proposals, ephemeral attach in chat, and PPT deck generation.',
    },
    {
      icon: 'PartitionOutlined',
      title: 'Document access control',
      desc: 'Define document access roles per file; retrieval filters by user-assigned roles. Super Admin manages categories; group admins manage roles and project user assignment.',
    },
    {
      icon: 'SearchOutlined',
      title: 'Hybrid retrieval',
      desc: 'BM25 + vector hybrid search with configurable alpha, MMR for diverse results, cosine threshold filtering, and optional GraphRAG (Neo4j) for entity-relationship reasoning.',
    },
  ],
  layers: [
    {
      name: 'Platform setup',
      desc: 'Super Admin provisions groups, categories, and users via JWT Auth Service. MongoDB config store holds projects, file pipeline state, usage metrics, and access audit logs.',
    },
    {
      name: 'Project creation',
      desc: 'Admins choose a knowledge connector (S3, Confluence, GitHub, Jira, Azure Wiki), configure embedding, vector store, retriever, guardrails, LLM, and optional GraphRAG. Validated projects become ACTIVE.',
    },
    {
      name: 'Knowledge ingestion',
      desc: 'Automated pipeline converts documents to Markdown, applies pre-embedding guardrails, chunks with AI-recommended strategies, embeds, and indexes into Milvus. Wiki structure chunking boosts hierarchy retrieval.',
    },
    {
      name: 'Request & response',
      desc: 'Queries pass input guardrails → supervisor routes to RAG or GraphRAG agent → context guardrails on retrieved chunks → LLM synthesis → output guardrails → streamed response with citations and used_sources.',
    },
    {
      name: 'Cost & budget control',
      desc: 'Track embedding tokens, LLM in/out, and pipeline costs per project. Enforce group, project, and user budget caps with extension requests and real-time budget warnings in chat.',
    },
  ],
  steps: [
    {
      title: 'Request admin access',
      desc: 'Contact Super Admin with your team name, group name, and expected user count. You will receive Admin credentials via secure onboarding email.',
    },
    {
      title: 'Configure your AI project',
      desc: 'Select a knowledge connector, verify credentials, configure embedding model, vector store, retriever (BM25 hybrid or semantic), guardrails, LLM, and optional GraphRAG.',
    },
    {
      title: 'Connect & process documents',
      desc: 'Sync from your connector or upload to S3. Enable the automated file pipeline (manual, scheduled, or event-driven). Monitor per-file status and apply role assignments.',
    },
    {
      title: 'Test your AI project',
      desc: 'Use Chat with streaming, deep-agent mode, and memory. Verify citations, test guardrails, try chat modes (Auto / RAG / GraphRAG), and validate role-filtered retrieval.',
    },
    {
      title: 'Monitor costs & budgets',
      desc: 'Review the admin budget dashboard, set group and user caps, approve extension requests, and track personal usage on My Usage.',
    },
  ],
  roles: [
    {
      icon: 'CrownOutlined',
      title: 'Super Admin',
      access: 'Full platform',
      accessClass: 'about-access-full',
      items: [
        'Provision groups & categories',
        'Manage users & group budget pools',
        'Issue starter kit activation codes',
        'Access guardrail audit & cost dashboards',
      ],
    },
    {
      icon: 'SettingOutlined',
      title: 'Admin',
      access: 'Group-scoped',
      accessClass: 'about-access-group',
      items: [
        'Create projects with any connector',
        'Configure pipelines & assign document roles',
        'Manage user budgets & extension requests',
        'Assign users to projects via RBAC',
      ],
    },
    {
      icon: 'UserOutlined',
      title: 'End User',
      access: 'Chat & usage',
      accessClass: 'about-access-read',
      items: [
        'Chat with streaming & citations',
        'View My Usage & request budget extensions',
        'Access role-filtered documents only',
      ],
    },
  ],
  limits: [
    { label: 'Max file size', value: '100 MB per file' },
    { label: 'Concurrent users', value: '15 simultaneous' },
    { label: 'Vector DB', value: 'Milvus only' },
    { label: 'LLM providers', value: 'OpenAI · Bedrock · Ollama' },
    { label: 'Data connectors', value: 'S3 · Confluence · GitHub · Jira · Azure Wiki' },
    { label: 'Language', value: 'English optimised' },
  ],
  roadmap: [
    { icon: 'ThunderboltOutlined', label: 'Self-service LLM onboarding' },
    { icon: 'RocketOutlined', label: 'Enhanced scalability' },
    { icon: 'DatabaseOutlined', label: 'Additional vector DB support' },
    { icon: 'TeamOutlined', label: 'Higher concurrent user capacity' },
    { icon: 'ApiOutlined', label: 'Gemini & Azure OpenAI providers' },
  ],
  starterKit: [
    { title: 'Standalone Chat Client', desc: 'Lightweight, project-specific chat application for local deployment with config.json setup.' },
    { title: 'One-Time Activation', desc: 'Secure activation using a unique project-bound activation code issued by admins.' },
    { title: 'Project-Bound Access', desc: 'Automatically connects users to the assigned project after activation.' },
    { title: 'Chat History & Document Access', desc: 'Full chat history management and inline document preview from indexed sources.' },
    { title: 'Customizable UI', desc: 'Light/Dark mode and accent color personalization.' },
    { title: 'Multi-LLM Support', desc: 'Supports OpenAI, Groq, and Ollama with connection testing.' },
    { title: 'Budget Monitoring', desc: 'Displays budget warnings and supports in-app extension requests.' },
    { title: 'Streaming & Guardrails Panel', desc: 'Live streaming responses with visible guardrail results and thinking steps.' },
  ],
  footer: 'CONFIDENTIAL · For internal distribution only · Enterprise AI Platform v2.0.0 · July 2026',
};
